import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import useSafeToast from '../utils/safeToast';
import { getApiUrl } from '../config/api';
import {
  FaPaperPlane,
  FaCheckCircle,
  FaCommentAlt,
  FaShieldAlt,
} from 'react-icons/fa';
import './FeedbackPage.css';

const recaptchaSiteKey = (process.env.REACT_APP_RECAPTCHA_SITE_KEY || '').trim();

const loadRecaptchaScript = () =>
  new Promise((resolve, reject) => {
    if (!recaptchaSiteKey) {
      resolve(false);
      return;
    }
    if (window.grecaptcha?.render) {
      resolve(true);
      return;
    }
    const existing = document.querySelector('script[data-recaptcha-script="true"]');
    if (existing) {
      existing.addEventListener('load', () => resolve(true), { once: true });
      existing.addEventListener('error', () => reject(new Error('Failed to load reCAPTCHA')), { once: true });
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.dataset.recaptchaScript = 'true';
    script.onload = () => resolve(true);
    script.onerror = () => reject(new Error('Failed to load reCAPTCHA'));
    document.body.appendChild(script);
  });

const FeedbackPage = () => {
  const toast = useSafeToast();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaReady, setCaptchaReady] = useState(!recaptchaSiteKey);
  const [captchaWidgetId, setCaptchaWidgetId] = useState(null);
  const [form, setForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  React.useEffect(() => {
    try {
      const storedUser = localStorage.getItem('user');
      if (!storedUser) return;
      const parsed = JSON.parse(storedUser);
      setForm((prev) => ({
        ...prev,
        name: prev.name || String(parsed?.name || '').trim(),
        email: prev.email || String(parsed?.email || '').trim(),
      }));
    } catch {
      // ignore invalid local user cache
    }
  }, []);

  React.useEffect(() => {
    let mounted = true;
    let widgetId = null;

    const mountCaptcha = async () => {
      try {
        const available = await loadRecaptchaScript();
        if (!mounted || !available || !recaptchaSiteKey) return;
        const container = document.getElementById('feedback-recaptcha');
        if (!container || !window.grecaptcha?.render) return;
        widgetId = window.grecaptcha.render(container, {
          sitekey: recaptchaSiteKey,
          callback: (token) => {
            setCaptchaToken(token || '');
          },
          'expired-callback': () => setCaptchaToken(''),
          'error-callback': () => setCaptchaToken(''),
        });
        setCaptchaWidgetId(widgetId);
        setCaptchaReady(true);
      } catch (error) {
        console.error('reCAPTCHA load failed:', error);
        if (mounted) setCaptchaReady(false);
      }
    };

    mountCaptcha();
    return () => {
      mounted = false;
      if (widgetId != null && window.grecaptcha?.reset) {
        window.grecaptcha.reset(widgetId);
      }
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.subject.trim() || !form.message.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (recaptchaSiteKey && !captchaToken) {
      toast.error('Please complete the reCAPTCHA challenge');
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('/api/feedback'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          subject: form.subject.trim(),
          message: form.message.trim(),
          recaptchaToken: captchaToken,
          website: '',
        }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to submit feedback');
      }

      setSubmitted(true);
      toast.success('Thank you! Your feedback has been submitted.');
      if (recaptchaSiteKey && captchaWidgetId != null && window.grecaptcha?.reset) {
        window.grecaptcha.reset(captchaWidgetId);
      }
      setCaptchaToken('');
    } catch (err) {
      toast.error(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitAnother = () => {
    setSubmitted(false);
    setForm({ name: '', email: '', subject: '', message: '' });
  };

  if (submitted) {
    return (
      <div className="feedback-page">
        <Navbar />
        <div className="feedback-page-content">
          <div className="feedback-success">
            <div className="success-icon">
              <FaCheckCircle />
            </div>
            <h1>Thank You!</h1>
            <p>Your feedback helps us improve UBSAAPP. We appreciate you taking the time to share your thoughts.</p>
            <button type="button" className="btn-submit-another" onClick={handleSubmitAnother}>
              Submit Another Feedback
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="feedback-page">
      <Navbar />
      <div className="feedback-page-content">
        <header className="feedback-header">
          <div className="feedback-header-badge">
            <FaCommentAlt />
          </div>
          <h1>Share Your Feedback</h1>
          <p>We would love to hear from you. Your input helps us make Book & Stay better for everyone.</p>
        </header>

        <div className="feedback-card">
          <form onSubmit={handleSubmit} className="feedback-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="name">Name *</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Your name"
                  value={form.name}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="subject">Subject *</label>
              <input
                id="subject"
                name="subject"
                type="text"
                placeholder="Enter feedback subject"
                value={form.subject}
                onChange={handleChange}
                maxLength={180}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="message">Your Feedback *</label>
              <textarea
                id="message"
                name="message"
                placeholder="Tell us what you think... What worked well? What could we improve?"
                value={form.message}
                onChange={handleChange}
                rows={5}
                maxLength={5000}
                required
              />
            </div>

            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              className="feedback-honeypot"
              aria-hidden="true"
              onChange={() => {}}
            />

            <div className="form-group">
              <label><FaShieldAlt /> Spam Protection</label>
              {recaptchaSiteKey ? (
                <div id="feedback-recaptcha" className="feedback-recaptcha" />
              ) : (
                <p className="feedback-captcha-note">Captcha is not configured for this environment.</p>
              )}
              {recaptchaSiteKey && !captchaReady && (
                <p className="feedback-captcha-note">Loading captcha...</p>
              )}
            </div>

            <button type="submit" className="btn-submit" disabled={loading}>
              {loading ? (
                <span className="btn-loading">
                  <span className="spinner" /> Submitting...
                </span>
              ) : (
                <>
                  <FaPaperPlane /> Submit Feedback
                </>
              )}
            </button>
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default FeedbackPage;
